import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as Logo } from "../../assets/icons/yna_logo_rect.svg";
import { ReactComponent as CheckIcon } from "../../assets/icons/check.svg";
import { Chip } from "../../ui/Chip/Chip";
import { Button } from "../../ui/Button/Button";
import { Typography } from "../../ui/Typography/Typography";
import { IconButton } from "../../ui/IconButton/IconButton";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ROUTES } from "../../constants/routes";
import { isMobileDevice } from "../../utils/isMobileDevice";
import classes from "./styles.module.scss";

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

const formatDateRu = (date: Date): string =>
  date.toLocaleDateString("ru-RU", DATE_FORMAT_OPTIONS) + " г.";

const getDefaultConnectionDate = (): string => formatDateRu(new Date());

const getDefaultNextBillingDate = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return formatDateRu(d);
};

const formatDateParam = (value: string | null): string | null => {
  if (!value) return null;
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return formatDateRu(parsedDate);
};

/** Формат данных с бэка — подставить ответ API вместо значений по умолчанию */
export interface PaymentThankYouData {
  connectionDate: string;
  nextBillingDate: string;
}

const PaymentThankYouPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobileOrTablet = isMobileDevice();

  const dateParam = searchParams.get("date");
  const shortTokenParam = searchParams.get("shortToken");
  const connectionDateParam = searchParams.get("connectionDate");
  const subscriptionPlanName = "YNA Bro";
  const subscriptionPlanLabel = searchParams.get("subscriptionPlanLabel") ?? "Тренер";
  
  useEffect(() => {
    console.log("PaymentThankYouPage query params", {
      date: dateParam,
      shortToken: shortTokenParam,
      cookie: document.cookie,
    });
  }, [dateParam, shortTokenParam, document.cookie]);

  const connectionDateFormatted =
    connectionDateParam != null
      ? formatDateRu(new Date(connectionDateParam))
      : getDefaultConnectionDate();

  const nextBillingDateFormatted =
    dateParam != null
      ? formatDateParam(dateParam) ?? getDefaultNextBillingDate()
      : getDefaultNextBillingDate();

  // Данные для отображения — заменить на использование PaymentThankYouData из API
  const pageData: PaymentThankYouData = {
    connectionDate: connectionDateFormatted,
    nextBillingDate: nextBillingDateFormatted,
  };

  const {
    connectionDate,
    nextBillingDate,
  } = pageData;

  const handleReturn = () => {
    navigate(ROUTES.DASHBOARD);
  };

  const handleClose = () => {
    navigate(ROUTES.DASHBOARD);
  };

  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <Logo className={classes.logo} />
        {!isMobileOrTablet && (
          <IconButton
            iconBtnsize="xl"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        )}
      </header>
      <div className={classes.card}>
        <main className={classes.content}>
          <div className={classes.titleBlock}>
            <Typography fsz={20} fw={500} className={classes.productName}>
              {subscriptionPlanName}
            </Typography>
            <Chip type="success" size="sm">
              {subscriptionPlanLabel}
            </Chip>
          </div>

          <Typography fsz={24} fw={500} className={classes.mainTitle}>
            Подписка активирована!
          </Typography>

          <div className={classes.checkIconWrap}>
            <CheckIcon className={classes.checkIcon} />
          </div>

          <div className={classes.details}>
            <Typography fsz={14} fw={400} className={classes.detailText}>
              Дата подключения {connectionDate}
            </Typography>
            <Typography fsz={14} fw={400} className={classes.detailText}>
              Следующее списание - <strong>{nextBillingDate}</strong> Вы можете отменить подписку в любой момент.
            </Typography>
          </div>

          {isMobileOrTablet ? (
            <Typography
              fsz={14}
              fw={400}
              className={classes.mobileCloseHint}
            >
              Закройте это окно, чтобы вернуться в приложение
            </Typography>
          ) : (
            <Button buttonSize="md" onClick={handleReturn}>
              Вернуться в календарь
            </Button>
          )}
        </main>
      </div>
    </div>
  );
};

export default PaymentThankYouPage;
